import React, { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { collection, query, where, onSnapshot, doc, Unsubscribe } from 'firebase/firestore';
import { db } from '@shared/services/firebase';
import { Account, UnifiedAccount, AccountMatchRecord, PayerInfo } from '@shared/types';
import { useLiveDataUid } from './useLiveDataUid';
import { logError } from '@/lib/log';

export interface ItemConnectionStatus {
    connectionStatus?: 'degraded' | 'revoked' | 'pending_expiration' | 'pending_disconnect';
    connectionError?: {
        code: string;
        message: string;
        at: string;
        isTransient: boolean;
    };
    lastSuccessfulSync?: string;
    institutionName?: string;
}

/**
 * Focused slice behind the DataContext facade: everything account- and
 * connection-shaped — Plaid asset accounts, unified liabilities, pending
 * account matches, connection health, and payer/designation info (uservars).
 *
 * NOTE: components should keep consuming useData() from DataContext for now —
 * demo mode (DemoProviders) only stubs the facade's DataContext.Provider, so a
 * component reading this context directly would crash under /demo.
 */
interface AccountsContextType {
    plaidAssets: Account[]; // Plaid asset accounts (checking/savings), enriched with designations
    unifiedLiabilities: UnifiedAccount[]; // Merged liability accounts (spinwheelUsers + plaidLiabilityAccounts → unifiedAccounts)
    pendingMatches: AccountMatchRecord[]; // Matches needing user review
    hasPendingConnections: boolean; // Whether user has unmatched Plaid accounts
    itemConnectionStatuses: Map<string, ItemConnectionStatus>; // item_id → connection health
    payerInfo: PayerInfo;
    monthlyIncomeOverride: number | null; // user-set monthly income (uservars), null = use estimate
}

export const AccountsContext = createContext<AccountsContextType | undefined>(undefined);

export const useAccountsContext = () => {
    const context = useContext(AccountsContext);
    if (!context) {
        throw new Error('useAccountsContext must be used within an AccountsProvider');
    }
    return context;
};

export const AccountsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const uid = useLiveDataUid();
    const [rawPlaidAssets, setRawPlaidAssets] = useState<Account[]>([]);
    const [accountDesignations, setAccountDesignations] = useState<{ paymentAccountId?: string; emergencyFundAccountId?: string }>({});
    const [unifiedLiabilities, setUnifiedLiabilities] = useState<UnifiedAccount[]>([]);
    const [pendingMatches, setPendingMatches] = useState<AccountMatchRecord[]>([]);
    const [hasPendingConnections, setHasPendingConnections] = useState<boolean>(false);
    const [itemConnectionStatuses, setItemConnectionStatuses] = useState<Map<string, ItemConnectionStatus>>(new Map());
    const [payerInfo, setPayerInfo] = useState<PayerInfo>({ status: 'none' });
    const [monthlyIncomeOverride, setMonthlyIncomeOverride] = useState<number | null>(null);

    // Enrich plaidAssets with account designations
    const plaidAssets = useMemo(() => {
        return rawPlaidAssets.map(acc => {
            const designations: ('payment' | 'emergency_fund')[] = [];
            if (accountDesignations.paymentAccountId === acc.id) designations.push('payment');
            if (accountDesignations.emergencyFundAccountId === acc.id) designations.push('emergency_fund');
            return designations.length > 0 ? { ...acc, designations } : acc;
        });
    }, [rawPlaidAssets, accountDesignations]);

    useEffect(() => {
        if (!uid) {
            setRawPlaidAssets([]);
            setAccountDesignations({});
            setUnifiedLiabilities([]);
            setPendingMatches([]);
            setHasPendingConnections(false);
            setItemConnectionStatuses(new Map());
            setPayerInfo({ status: 'none' });
            setMonthlyIncomeOverride(null);
            return;
        }

        const unsubscribes: Unsubscribe[] = [];

        // Fetch Plaid asset accounts (checking/savings) from plaidAccounts collection
        // plaidAccounts is populated from transactions_sync response with balance data
        const plaidAssetsQuery = query(
            collection(db, 'plaidAccounts'),
            where('uid', '==', uid)
        );
        unsubscribes.push(onSnapshot(plaidAssetsQuery, (snapshot) => {
            const fetchedAssets: Account[] = snapshot.docs
                .filter(doc => {
                    const data = doc.data();
                    const type = (data.type || '').toLowerCase();
                    // Only include depository accounts (checking/savings/cd/money market)
                    return type === 'depository';
                })
                .map(doc => {
                    const data = doc.data();
                    const subtype = (data.subtype || '').toLowerCase();

                    let accountType: 'checking' | 'savings' = 'checking';
                    if (subtype === 'savings' || subtype === 'money market' || subtype === 'cd') {
                        accountType = 'savings';
                    }

                    return {
                        id: doc.id,
                        account_id: data.account_id || undefined,
                        item_id: data.item_id || undefined,
                        name: data.name || data.official_name || 'Unknown Account',
                        balance: Number(data.balance || 0),
                        type: accountType,
                        institution: data.institution_name || 'Unknown Institution',
                        mask: data.mask || undefined
                    };
                });

            setRawPlaidAssets(fetchedAssets);
        }, (error) => {
            logError(error, { context: 'fetch_plaid_assets' });
        }));

        // Subscribe to plaidItems for connection health status
        const plaidItemsQuery = query(
            collection(db, 'plaidItems'),
            where('uid', '==', uid)
        );
        unsubscribes.push(onSnapshot(plaidItemsQuery, (snapshot) => {
            const statuses = new Map<string, ItemConnectionStatus>();
            snapshot.docs.forEach(doc => {
                const data = doc.data();
                const itemId = data.item_id;
                if (!itemId) return;
                // Only include items that have connection issues or status fields
                const status: ItemConnectionStatus = {
                    institutionName: data.institution_name,
                    lastSuccessfulSync: data.lastSuccessfulSync?.toDate?.()?.toISOString(),
                };
                if (data.connectionStatus) status.connectionStatus = data.connectionStatus;
                if (data.connectionError) {
                    status.connectionError = {
                        code: data.connectionError.code || '',
                        message: data.connectionError.message || '',
                        at: data.connectionError.at?.toDate?.()?.toISOString() || '',
                        isTransient: data.connectionError.isTransient || false,
                    };
                }
                statuses.set(itemId, status);
            });
            setItemConnectionStatuses(statuses);
        }, (error) => {
            logError(error, { context: 'fetch_plaid_items' });
        }));

        // Listen for pending connection status
        const connectionStatusRef = doc(db, 'plaidConnectionStatus', uid);
        unsubscribes.push(onSnapshot(connectionStatusRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.data();
                setHasPendingConnections(data.has_pending_connections === true);
            } else {
                setHasPendingConnections(false);
            }
        }, (error) => {
            logError(error, { context: 'fetch_connection_status' });
            setHasPendingConnections(false);
        }));

        // Fetch unified liabilities
        const unifiedQuery = query(
            collection(db, 'unifiedAccounts'),
            where('uid', '==', uid)
        );
        unsubscribes.push(onSnapshot(unifiedQuery, (snapshot) => {
            const fetched: UnifiedAccount[] = snapshot.docs
                .map(d => {
                    const data = d.data();
                    return { ...data, id: d.id } as UnifiedAccount;
                })
                .filter(a => a.status !== 'deleted')
                .filter(a => a.accountType !== 'checking' && a.accountType !== 'savings');
            setUnifiedLiabilities(fetched);
        }, (error) => {
            logError(error, { context: 'fetch_unified_liabilities' });
            setUnifiedLiabilities([]);
        }));

        // Fetch pending matches needing user review
        const matchesQuery = query(
            collection(db, 'accountMatches'),
            where('uid', '==', uid),
            where('reviewedByUser', '==', false)
        );
        unsubscribes.push(onSnapshot(matchesQuery, (snapshot) => {
            const fetched: AccountMatchRecord[] = snapshot.docs.map(d => {
                const data = d.data();
                return { ...data, id: d.id } as AccountMatchRecord;
            });
            setPendingMatches(fetched);
        }, (error) => {
            logError(error, { context: 'fetch_pending_matches' });
            setPendingMatches([]);
        }));

        // Subscribe to uservars for payer info and account designations
        unsubscribes.push(onSnapshot(doc(db, 'uservars', uid), (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.data();
                // Resolve payer info from verifiedPayers map for the active account
                const checkingDocId = data.payerCheckingDocId || undefined;
                const verifiedPayers = data.verifiedPayers || {};
                const activePayer = checkingDocId ? verifiedPayers[checkingDocId] : undefined;

                setPayerInfo({
                    payerId: activePayer?.payerId || data.spinwheelPayerId || undefined,
                    status: activePayer?.status || data.payerStatus || 'none',
                    bankMask: activePayer?.bankMask || data.payerBankMask || undefined,
                    bankName: activePayer?.bankName || data.payerBankName || undefined,
                    accountId: data.paymentAccountId || undefined,
                    checkingDocId,
                    needsAuth: data.payerNeedsAuth || false,
                    authItemId: data.payerAuthItemId || undefined,
                });
                setAccountDesignations({
                    paymentAccountId: data.paymentAccountId || undefined,
                    emergencyFundAccountId: data.emergencyFundAccountId || undefined,
                });
                setMonthlyIncomeOverride(
                    typeof data.monthlyIncomeOverride === 'number' && data.monthlyIncomeOverride > 0
                        ? data.monthlyIncomeOverride
                        : null,
                );
            } else {
                setPayerInfo({ status: 'none' });
                setAccountDesignations({});
                setMonthlyIncomeOverride(null);
            }
        }, (error) => {
            logError(error, { context: 'fetch_uservars' });
        }));

        return () => {
            unsubscribes.forEach(unsubscribe => unsubscribe());
        };
    }, [uid]);

    const value = useMemo(() => ({
        plaidAssets,
        unifiedLiabilities,
        pendingMatches,
        hasPendingConnections,
        itemConnectionStatuses,
        payerInfo,
        monthlyIncomeOverride,
    }), [plaidAssets, unifiedLiabilities, pendingMatches, hasPendingConnections, itemConnectionStatuses, payerInfo, monthlyIncomeOverride]);

    return (
        <AccountsContext.Provider value={value}>
            {children}
        </AccountsContext.Provider>
    );
};
